import { useApolloClient } from '@apollo/client';
import { useState, useCallback, useRef, useEffect } from 'react';
import { Animated, Platform } from 'react-native';

import { ChecklistTemplateX } from '@components/ProjectDetail/ProjectChecklist/ChecklistTemplateItem';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import {
  ListProjectChecklistDocument,
  useApplyProjectChecklistTemplateMutation,
  useListProjectChecklistLazyQuery,
  useToggleChecklistTemplateFavoriteMutation,
  ListChecklistTemplatesDocument,
} from '@graphql/generated';

export const sortChecklistTemplates = (templates: ChecklistTemplateX[]) => {
  const templatesWithSortedItems = templates.map((template) => ({
    ...template,
    checklistTemplateItems: [...template.checklistTemplateItems].sort((a, b) =>
      a.checklistTemplateItemName.localeCompare(b.checklistTemplateItemName)
    ),
  }));

  return templatesWithSortedItems.sort((a, b) => {
    if (a.isFavorited !== b.isFavorited) {
      return b.isFavorited ? 1 : -1;
    }

    if (a.isFavorited && b.isFavorited) {
      const dateA = a.favoritedAt ? new Date(a.favoritedAt).getTime() : 0;
      const dateB = b.favoritedAt ? new Date(b.favoritedAt).getTime() : 0;

      if (dateA === 0 && dateB === 0) return 0;
      if (dateA === 0) return 1;
      if (dateB === 0) return -1;

      return dateB - dateA;
    }

    return a.templateName.localeCompare(b.templateName);
  });
};

export const useChecklistTemplate = (projectUUId: string) => {
  const client = useApolloClient();
  const isMountedRef = useRef(true);

  const { setIdProjectChecklistTemplateOpen } = useWebDrawer();

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const safeSetState = (setFn) => {
    if (isMountedRef.current) {
      setFn();
    }
  };

  // State management
  const [expandedId, setExpandedId] = useState(undefined);
  const [creatingChecklist, setCreatingChecklist] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [skippedItems, setSkippedItems] = useState([]);
  const [checklistItems, setChecklistItems] = useState<ChecklistTemplateX[]>(
    []
  );
  const [toChecklistItemsParams, setToChecklistItemsParams] = useState<any[]>(
    []
  );

  // Animation refs
  const animationRefs = useRef({});

  // GraphQL mutations and queries
  const [listProjectChecklist] = useListProjectChecklistLazyQuery();
  const [applyProjectChecklistTemplateMutation] =
    useApplyProjectChecklistTemplateMutation();
  const [toggleChecklistTemplateFavoriteMutation] =
    useToggleChecklistTemplateFavoriteMutation();

  // Expand/collapse functionality
  const toggleExpand = useCallback((id) => {
    setExpandedId((current) => (current === id ? undefined : id));
  }, []);

  // Item selection functionality
  const toggleItemChecked = useCallback((checklistId, itemId) => {
    const toggleArrayItem = (arr, item) =>
      arr.includes(item) ? arr.filter((i) => i !== item) : [...arr, item];

    setChecklistItems((prevChecklists) =>
      prevChecklists.map((checklist) =>
        checklist.id === checklistId
          ? {
              ...checklist,
              itemsChecked: toggleArrayItem(checklist.itemsChecked, itemId),
            }
          : checklist
      )
    );
  }, []);

  const toggleItemCheckedAll = useCallback((checklistId, isClear = false) => {
    setChecklistItems((prevChecklists) =>
      prevChecklists.map((checklist) =>
        checklist.id === checklistId
          ? {
              ...checklist,
              itemsChecked: isClear
                ? []
                : checklist.checklistTemplateItems.map((t) => t.id),
            }
          : checklist
      )
    );
  }, []);

  // Favorite functionality
  const toggleFavorite = useCallback((checklistId) => {
    setChecklistItems((prevChecklists) =>
      sortChecklistTemplates(
        prevChecklists.map((checklist) => {
          if (checklist.id === checklistId) {
            return {
              ...checklist,
              isFavorited: !checklist.isFavorited,
              favoritedAt: checklist.isFavorited
                ? null
                : new Date(Date.now()).toISOString(),
            };
          }
          return checklist;
        })
      )
    );

    toggleChecklistTemplateFavoriteMutation({
      variables: { checklistTemplateId: checklistId },
      refetchQueries: [ListChecklistTemplatesDocument],
    });
  }, []);

  // Animation management
  const getAnimation = useCallback((id) => {
    if (!animationRefs.current[id]) {
      animationRefs.current[id] = new Animated.Value(0);
    }
    return animationRefs.current[id];
  }, []);

  useEffect(() => {
    if (expandedId) {
      Animated.timing(getAnimation(expandedId), {
        toValue: 1,
        duration: 300,
        useNativeDriver: false,
      }).start();
    }

    const prevExpandedId = Object.keys(animationRefs.current).find(
      (key) => animationRefs.current[key].__getValue() === 1
    );

    if (prevExpandedId && prevExpandedId !== expandedId) {
      Animated.timing(getAnimation(prevExpandedId), {
        toValue: 0,
        duration: 300,
        useNativeDriver: false,
      }).start();
    }
  }, [expandedId, getAnimation]);

  // Checklist creation and application
  const doApplyProjectChecklist = async (params, navigation?: any) => {
    if (params.length > 0) {
      safeSetState(() => setCreatingChecklist(true));
      for (const param of params) {
        await applyProjectChecklistTemplateMutation({
          variables: param,
          onError: (error) => {
            console.log('applyProjectChecklistTemplateMutation error, ', error);
          },
        });
      }
      if (Platform.OS === 'web') {
        safeSetState(() => setCreatingChecklist(false));
        setIdProjectChecklistTemplateOpen('-1');
        return;
      }

      await client.refetchQueries({
        include: [ListProjectChecklistDocument],
      });
      if (Platform.OS !== 'web' && navigation) goToProjectChecklist(navigation);
    }

    safeSetState(() => setCreatingChecklist(false));
    return params;
  };

  const createChecklist = async (navigation?: any) => {
    setCreatingChecklist(true);
    const existedChecklistData = await listProjectChecklist({
      variables: { projectUUId },
    });

    let params = [];
    const createItems = [];

    const templateListHasChecked = checklistItems.filter(
      (template) => template.itemsChecked.length > 0
    );
    if (existedChecklistData.data?.listProjectChecklist.length === 0) {
      params = templateListHasChecked.map(({ id, itemsChecked }) => ({
        uuid: projectUUId,
        projectChecklistTemplateId: id,
        selectedItemIds: itemsChecked,
      }));
    } else {
      templateListHasChecked.forEach((template) => {
        const selectedItems = template.checklistTemplateItems.filter((t) =>
          template.itemsChecked.includes(t.id)
        );
        const selectedItemIds = [];

        selectedItems.forEach((item) => {
          if (
            existedChecklistData.data?.listProjectChecklist.some(
              (t) => t.name === item.checklistTemplateItemName
            )
          ) {
            createItems.push(item.checklistTemplateItemName);
          } else {
            selectedItemIds.push(item.id);
          }
        });

        if (selectedItemIds.length > 0) {
          params.push({
            uuid: projectUUId,
            projectChecklistTemplateId: template.id,
            selectedItemIds,
          });
        }
      });
    }

    if (createItems.length > 0) {
      setToChecklistItemsParams(params);
      setCreatingChecklist(false);
      setSkippedItems(createItems);
      setModalVisible(true);
    } else {
      await doApplyProjectChecklist(params, navigation);
    }
  };
  const goToProjectChecklist = useCallback(
    (navigation) => {
      navigation.push('projects-stack', {
        screen: 'project-checklist',
        params: { projectUUId },
      });
    },
    [projectUUId]
  );

  const doCloseModal = async (navigation?: any) => {
    setModalVisible(false);
    setSkippedItems([]);
    if (toChecklistItemsParams.length > 0) {
      await doApplyProjectChecklist(toChecklistItemsParams, navigation);
    }
  };

  // Utility functions
  const totalCheckedCount = checklistItems.reduce(
    (total, checklist) => total + (checklist.itemsChecked?.length || 0),
    0
  );

  return {
    // State
    expandedId,
    creatingChecklist,
    modalVisible,
    skippedItems,
    checklistItems,
    toChecklistItemsParams,

    // Setters
    setChecklistItems,
    setModalVisible,

    // Functions
    toggleExpand,
    toggleItemChecked,
    toggleItemCheckedAll,
    toggleFavorite,
    getAnimation,
    createChecklist,
    doCloseModal,
    goToProjectChecklist,
    totalCheckedCount,

    // Utility
    sortChecklistTemplates,
  };
};
